import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useHistory } from 'react-router';
import { Waypoint } from 'react-waypoint';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useSelectionState } from '../../../../hooks/useSelectionState';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	invalidateAllServiceJobFilters,
	invalidateAllServiceJobSummary,
	useExportServiceJobsMutation,
	useGetExternalIntegrationsQuery,
	useServiceJobContactsMutation,
	useServiceJobFilter,
	useServiceJobSummaryQuery,
} from '../../../../queries';
import { BreadcrumbBar } from '../../../components-v2/BreadcrumbBar';
import { Column, Page } from '../../../components-v2/PageLayout';
import { Checkbox } from '../../../components-v2/Checkbox';
import { EntityChip } from '../../../components/chips/EntityChip';
import {
	Databoard,
	DataboardActions,
	DataboardBody,
	DataboardFilters,
	DataboardNoResults,
	DataboardStat,
	DataboardStatLabel,
	DataboardStats,
	DataboardStatTitle,
	DataboardStatValue,
} from '../../../components/Databoard';
import { DeleteModal, DeleteType } from '../../../components/dataBoards/DeleteModal';
import { MoneyFilterDropdown } from '../../../components/dataBoards/MoneyFilterDropdown';
import { ServiceJobDateFilterDropdown } from '../../../components/dataBoards/serviceJobs/ServiceJobDateFilterDropdown';
import { ServiceJobDetailsFlyout } from '../../../components/dataBoards/serviceJobs/ServiceJobDetailsFlyout';
import { ServiceJobStatusFilterDropdown } from '../../../components/dataBoards/serviceJobs/ServiceJobStatusFilterDropdown';
import { EditServiceJobModal } from '../../../components/dataBoards/serviceJobs/EditServiceJobModal';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../components/DataGrid';
import { ExportConfirmationModalV2 } from '../../../components/ExportConfirmation';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { CloseButton } from '../../../components/CloseButton';
import { LightningBulbIcon } from '../../../components/svgs/icons/LightBulbIcon';
import { InputGroupAddon, InputGroupInput, InputGroupV2 as InputGroup } from '../../../components-v2/InputGroup';
import { SearchIcon } from '../../../components/svgs/icons/SearchIcon';
import { UsersActionBar } from '../../../components/UsersActionBar';
import { ROUTES } from '../../../constants/routes';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet as dbStyleSheet } from '../styles';
import { styleSheet } from './styles';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
	DropdownTrigger,
} from '../../../components-v2/Dropdown';
import { Button } from '../../../components-v2/Button';
import { ButtonGroup, ButtonGroupSeparator } from '../../../components-v2/ButtonGroup';
import { ChevronDown } from '../../../components/svgs/icons/ChevronDown';
import {
	SERVICE_JOB_PROVIDERS,
	formatServiceJobCurrency,
	formatServiceJobDate,
} from '../../../components/dataBoards/serviceJobs/utils';
import { Drawer } from '../../../components-v2/Drawer';

enum MenuItem {
	Edit = 'Edit',
	Delete = 'Delete',
}

export const ServiceJobsBoard = () => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const { logApiError } = useEventLogging('ServiceJobsBoard');

	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({ impersonationContext: undefined });
	const hasServiceJobIntegration = externalIntegrations?.some(
		integration => integration.enabled && SERVICE_JOB_PROVIDERS.includes(integration.provider)
	);
	const canEdit = !hasServiceJobIntegration;
	const VIEW_ONLY_KEY = 'ViewOnlyMode_ServiceJobs_Alert';
	const [showViewOnlyBanner, setShowViewOnlyBanner] = React.useState(
		() => localStorage.getItem(VIEW_ONLY_KEY) !== 'false'
	);

	const onCloseViewOnlyBanner = () => {
		setShowViewOnlyBanner(false);
		localStorage.setItem(VIEW_ONLY_KEY, 'false');
	};

	// Search/Filter state
	const [searchFieldValue, setSearchFieldValue] = React.useState('');
	const debouncedSearchFieldValue = useDebounceValue(searchFieldValue, 500);

	// Filter dropdowns state
	const [selectedStatuses, setSelectedStatuses] = React.useState<Api.IServiceJobStatus[]>([]);
	const [selectedMinimumDate, setSelectedMinimumDate] = React.useState<string | null>(null);
	const [selectedMaximumDate, setSelectedMaximumDate] = React.useState<string | null>(null);
	const [minimumTotal, setMinimumTotal] = React.useState<string>('');
	const [maximumTotal, setMaximumTotal] = React.useState<string>('');

	// Modal state
	const [jobToEdit, setJobToEdit] = React.useState<Partial<Api.IServiceJob> | null>(null);
	const [jobToDelete, setJobToDelete] = React.useState<Api.IServiceJob | null>(null);
	const [isEditOpen, setIsEditOpen] = React.useState(false);
	const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);

	// Flyout state
	const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
	const [jobToView, setJobToView] = React.useState<Api.IServiceJob | null>(null);

	const hasSearchValues =
		!!debouncedSearchFieldValue ||
		selectedStatuses.length > 0 ||
		!!selectedMinimumDate ||
		!!minimumTotal ||
		!!maximumTotal;

	// Build filter criteria
	const filterRequest = React.useMemo(() => {
		const criteria: Api.IServiceJobFilterCriteria[] = [];

		if (debouncedSearchFieldValue) {
			criteria.push({ property: Api.ServiceJobFilterProperty.JobNumber, value: debouncedSearchFieldValue });
		}

		if (selectedStatuses.length === 1) {
			criteria.push({ property: Api.ServiceJobFilterProperty.JobStatus, value: selectedStatuses[0].name });
		} else if (selectedStatuses.length > 1) {
			criteria.push({
				op: 'Or',
				criteria: selectedStatuses.map(status => ({
					property: Api.ServiceJobFilterProperty.JobStatus,
					value: status.name,
				})),
			});
		}

		if (selectedMinimumDate) {
			const dayBefore = moment(selectedMinimumDate).subtract(1, 'day').format('YYYY-MM-DD');
			criteria.push({ property: Api.ServiceJobFilterProperty.CompletedOn, value: dayBefore, op: 'Gt' });
		}
		if (selectedMaximumDate) {
			const dayAfter = moment(selectedMaximumDate).add(1, 'day').format('YYYY-MM-DD');
			criteria.push({ property: Api.ServiceJobFilterProperty.CompletedOn, value: dayAfter, op: 'Lt' });
		}

		if (minimumTotal) {
			criteria.push({ property: Api.ServiceJobFilterProperty.Total, value: minimumTotal, op: 'Gt' });
		}
		if (maximumTotal) {
			criteria.push({ property: Api.ServiceJobFilterProperty.Total, value: maximumTotal, op: 'Lt' });
		}

		return { criteria };
	}, [
		debouncedSearchFieldValue,
		selectedStatuses,
		selectedMinimumDate,
		selectedMaximumDate,
		minimumTotal,
		maximumTotal,
	]);

	// Query hooks
	const jobFilterQuery = useServiceJobFilter({
		filterRequest,
		pageSize: 25,
		enabled: true,
	});

	const jobSummaryQuery = useServiceJobSummaryQuery({
		filterRequest,
		enabled: true,
	});

	const exportJobsMutation = useExportServiceJobsMutation({
		onError: error => {
			logApiError('ExportServiceJobs-Error', error);
		},
	});

	const jobContactsMutation = useServiceJobContactsMutation();

	// Selection state
	const jobs = React.useMemo(() => {
		return jobFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [jobFilterQuery.data?.pages]);

	const {
		excludedRows: excludedJobs,
		selectedRows: selectedJobs,
		selectionState: jobSelectionState,
		isPartialSelected,
		isSelectionStateAll,
		isSelected: isJobSelected,
		reset: resetJobSelection,
		toggleRow: toggleJobRow,
		toggleSelectAll: toggleSelectAllJobs,
	} = useSelectionState({ rows: jobs?.map(j => j.id).filter((id): id is string => !!id) });

	const [actionButtonsDisabled, setActionButtonsDisabled] = React.useState(false);

	const refreshJobs = () => {
		invalidateAllServiceJobFilters();
		invalidateAllServiceJobSummary();
	};

	const dedupeContacts = (contacts: Api.IProjectedContact[]) => {
		return contacts.filter((contact, index) => contacts.findIndex(c => c.id === contact.id) === index);
	};

	const excludedContacts = React.useMemo(() => {
		return dedupeContacts(
			excludedJobs
				.map(jobId => {
					const job = jobs.find(j => j.id === jobId);
					return job?.contact ? job.contact : null;
				})
				.filter((contact): contact is Api.IProjectedContact => contact != null)
		);
	}, [excludedJobs, jobs]);

	const selectedContacts = React.useMemo(() => {
		return dedupeContacts(
			jobs
				.filter(job => job.id && selectedJobs.indexOf(job.id) > -1)
				.map(job => job.contact)
				.filter((contact): contact is Api.IProjectedContact => contact != null)
		);
	}, [jobs, selectedJobs]);

	const getMatchingContacts = async () => {
		setActionButtonsDisabled(true);

		let results: Api.IProjectedContact[] = [];

		if (isSelectionStateAll) {
			try {
				const excludedContactsCriteria = excludedContacts
					.filter((contact): contact is Api.IProjectedContact & { id: string } => !!contact.id)
					.map(contact => ({
						property: Api.ServiceJobFilterProperty.ContactId,
						op: 'Not' as const,
						value: contact.id,
					}));
				const apiResults = await jobContactsMutation.mutateAsync({
					filterRequest: {
						criteria: [...(filterRequest.criteria || []), ...excludedContactsCriteria],
					},
				});

				const excludedContactIdsMap = new Set(excludedContacts.map(contact => contact.id));
				results = apiResults.filter(contact => !excludedContactIdsMap.has(contact.id));
			} catch (error) {
				logApiError('ResolveContacts-Error', error);
			}
		} else {
			results = dedupeContacts(
				jobs
					.filter(job => job.id && selectedJobs.indexOf(job.id) > -1)
					.map(job => job.contact)
					.filter((contact): contact is Api.IProjectedContact => contact != null)
			);
		}

		setActionButtonsDisabled(false);
		return results;
	};

	const onExport = () => {
		exportJobsMutation.mutate({
			filterRequest: {
				...filterRequest,
				...(jobSelectionState === Api.ESelectionState.Some
					? { includeIds: selectedJobs }
					: jobSelectionState === Api.ESelectionState.All
						? { excludeIds: excludedJobs }
						: {}),
			},
		});
	};

	const clearSearch = () => {
		// Clearing the filter state changes filterRequest (and thus the query keys), so react-query
		// refetches the list and summary automatically — no manual invalidation needed.
		setSearchFieldValue('');
		setSelectedStatuses([]);
		setSelectedMinimumDate(null);
		setSelectedMaximumDate(null);
		setMinimumTotal('');
		setMaximumTotal('');
	};

	const onSearchFieldInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchFieldValue(e.target.value);
	};

	const addJobClicked = () => {
		setJobToEdit({});
		setIsEditOpen(true);
	};

	const viewJobDetails = (job: Api.IServiceJob) => {
		setJobToView(job);
		setIsDetailsOpen(true);
	};

	const editJob = (job: Api.IServiceJob) => {
		setJobToEdit(job);
		setIsEditOpen(true);
	};

	const deleteJob = (job: Api.IServiceJob) => {
		setJobToDelete(job);
		setIsDeleteOpen(true);
	};

	const onDeleteConfirmed = () => {
		setIsDeleteOpen(false);
		setJobToDelete(null);
		refreshJobs();
	};

	const onEditSave = () => {
		setIsEditOpen(false);
		setJobToEdit(null);
		refreshJobs();
	};

	const onEditCancel = () => {
		setIsEditOpen(false);
		setJobToEdit(null);
	};

	const onSendEmailClicked = async () => {
		const matchingContacts = await getMatchingContacts();

		fullscreenModal.history?.push({
			pathname: '/email/campaigns/create/from-selection',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						bulkFilterRequestOptions: {
							readOnlySearchCriteria: true,
						},
						contacts: matchingContacts.map(c => new Api.ContactViewModel(userSession, c)),
					},
					type: 'Contacts',
				},
			},
		});
	};

	const isLoading = jobFilterQuery.isPending;
	const noResultsFound = !isLoading && jobFilterQuery.isFetched && jobs.length === 0;

	return (
		<Page>
			<MultiContainerHeader
				appBarHeader={
					<BreadcrumbBar
						items={[
							{ label: 'Data Boards', path: ROUTES.dataBoards.home },
							{ label: 'Service Jobs', path: ROUTES.dataBoards.serviceJobs },
						]}
					/>
				}
			/>

			<Column styles={[styleSheet.container]}>
				<Databoard styles={[styleSheet.databoardContainer]}>
					{!canEdit && showViewOnlyBanner ? (
						<div className={css(styleSheet.viewOnlyModeBannerContainer)}>
							<div className={css(styleSheet.viewOnlyModeBannerContent)}>
								<LightningBulbIcon />
								<div>
									<h4 className={css(styleSheet.infoHeaderContentBold)}>View Only Mode</h4>
									<div>
										When a service job integration is enabled you cannot make edits to service job data through
										Levitate.
									</div>
								</div>
							</div>
							<CloseButton onClick={onCloseViewOnlyBanner} />
						</div>
					) : null}

					<DataboardFilters>
						<InputGroup styles={[styleSheet.searchInput]}>
							<InputGroupAddon align='inlineStart'>
								<SearchIcon />
							</InputGroupAddon>
							<InputGroupInput
								id='service-job-search-input'
								type='text'
								value={searchFieldValue}
								onChange={onSearchFieldInputChanged}
								placeholder='Search by job number...'
							/>
						</InputGroup>

						<ServiceJobStatusFilterDropdown
							selectedStatuses={selectedStatuses}
							setSelectedStatuses={setSelectedStatuses}
							anchorClassName={css(dbStyleSheet.dropdownReset)}
							styles={[dbStyleSheet.mediumInput]}
						/>

						<div className={css(dbStyleSheet.mediumInput)}>
							<ServiceJobDateFilterDropdown
								selectedMinimumDate={selectedMinimumDate}
								setSelectedMinimumDate={setSelectedMinimumDate}
								selectedMaximumDate={selectedMaximumDate}
								setSelectedMaximumDate={setSelectedMaximumDate}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<MoneyFilterDropdown
								key={`totalFilterDropdown-${minimumTotal}-${maximumTotal}`}
								initialMinimum={minimumTotal}
								initialMaximum={maximumTotal}
								onChange={value => {
									setMinimumTotal(value.minimum);
									setMaximumTotal(value.maximum);
								}}
								fieldName='Total'
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						{hasSearchValues && (
							<Button variant='secondary' styles={[dbStyleSheet.clearSearchButton]} onClick={clearSearch}>
								Clear Search
							</Button>
						)}
					</DataboardFilters>

					<DataboardActions>
						<div className={css(dbStyleSheet.leftHeader)}>
							<UsersActionBar
								excludedContacts={excludedContacts}
								selectedContacts={selectedContacts}
								getMatchingContacts={getMatchingContacts}
								onExport={onExport}
								selectionState={jobSelectionState}
								actionButtonsDisabled={actionButtonsDisabled}
								onSendEmailClicked={onSendEmailClicked}
								showKeepInTouch={false}
							/>
						</div>

						{canEdit ? (
							<div className={css(dbStyleSheet.rightHeader)}>
								<ButtonGroup>
									<Button variant='primary' onClick={addJobClicked}>
										Add Service Job
									</Button>
									<ButtonGroupSeparator />
									<Dropdown>
										<DropdownTrigger render={<Button variant='primary' aria-label='More add options' />}>
											<ChevronDown />
										</DropdownTrigger>
										<DropdownContent align='end'>
											<DropdownItem onClick={() => history.push(ROUTES.dataBoards.serviceJobsImport)}>
												Import from CSV
											</DropdownItem>
										</DropdownContent>
									</Dropdown>
								</ButtonGroup>
							</div>
						) : null}
					</DataboardActions>

					<DataboardBody data-scroll-restoration-id='service-jobs-page-table' hasFooter>
						<DataGrid styles={[dbStyleSheet.tableOverrides]} isLoading={isLoading}>
							<DataGridColumns>
								<DataGridColumn sticky styles={[dbStyleSheet.checkboxColumn]}>
									<Checkbox
										checked={isSelectionStateAll ? true : isPartialSelected ? 'indeterminate' : false}
										id='select-all-service-jobs'
										onCheckedChange={() => {
											resetJobSelection();
											toggleSelectAllJobs();
										}}
										size='lg'
									/>
								</DataGridColumn>
								<DataGridColumn sticky>Contact</DataGridColumn>
								<DataGridColumn sticky>Job Number</DataGridColumn>
								<DataGridColumn sticky>Summary</DataGridColumn>
								<DataGridColumn sticky>Status</DataGridColumn>
								<DataGridColumn sticky>Total</DataGridColumn>
								<DataGridColumn sticky>Completed On</DataGridColumn>
								<DataGridColumn sticky>&nbsp;</DataGridColumn>
							</DataGridColumns>

							<DataGridRows>
								{!isLoading
									? jobs.map(job => (
											<DataGridRow key={`service-job-${job.id}`} styles={[dbStyleSheet.row]}>
												<DataGridRowItem>
													<Checkbox
														checked={isJobSelected(job.id ?? '')}
														id={job.id ?? ''}
														onClick={event => {
															event.stopPropagation();
															event.preventDefault();
															if (job.id) {
																toggleJobRow(job.id, event.shiftKey);
															}
														}}
														size='lg'
													/>
												</DataGridRowItem>
												<DataGridRowItem>
													{job.contact ? (
														<EntityChip
															className={css(dbStyleSheet.contact)}
															key={job.contact.id}
															entity={new Api.ContactViewModel(userSession, job.contact)}
															readOnly={true}
															sizeConstraint='compact'
														/>
													) : null}
												</DataGridRowItem>
												<DataGridRowItem>
													<Button
														variant='link'
														styles={[styleSheet.jobNumberButton]}
														onClick={() => viewJobDetails(job)}
													>
														<h6 className={css(dbStyleSheet.name, baseStyleSheet.truncateText)}>{job.jobNumber}</h6>
													</Button>
												</DataGridRowItem>
												<DataGridRowItem title={job.summary || undefined}>
													<div className={css(styleSheet.summary)}>{job.summary || '-'}</div>
												</DataGridRowItem>
												<DataGridRowItem>{job.jobStatus?.name || '-'}</DataGridRowItem>
												<DataGridRowItem>{formatServiceJobCurrency(job.total)}</DataGridRowItem>
												<DataGridRowItem>{formatServiceJobDate(job.completedOn)}</DataGridRowItem>
												<DataGridRowItem>
													{canEdit && job.canModify ? (
														<Dropdown>
															<DropdownEllipsisTrigger />
															<DropdownContent>
																<DropdownItem onClick={() => editJob(job)}>{MenuItem.Edit}</DropdownItem>
																<DropdownItem onClick={() => deleteJob(job)}>{MenuItem.Delete}</DropdownItem>
															</DropdownContent>
														</Dropdown>
													) : null}
												</DataGridRowItem>
											</DataGridRow>
										))
									: null}

								{isLoading ? (
									<DataGridRow>
										<DataGridRowItem colSpan={8}>
											<LoadingSpinner type='large' />
										</DataGridRowItem>
									</DataGridRow>
								) : null}
							</DataGridRows>
						</DataGrid>

						{!jobFilterQuery.isPending && !jobFilterQuery.isFetchingNextPage && jobFilterQuery.hasNextPage ? (
							<Waypoint bottomOffset='-200px' onEnter={() => jobFilterQuery.fetchNextPage()} />
						) : null}

						{noResultsFound ? (
							<DataboardNoResults onClearSearch={clearSearch} showClearSearch={hasSearchValues} />
						) : null}
					</DataboardBody>

					<DataboardStats>
						<DataboardStatTitle>Service Job Stats</DataboardStatTitle>
						<DataboardStat>
							<DataboardStatLabel>Total Jobs</DataboardStatLabel>
							<DataboardStatValue>{jobSummaryQuery.data?.totalJobs || 0}</DataboardStatValue>
						</DataboardStat>

						<DataboardStat>
							<DataboardStatLabel>Completed Jobs</DataboardStatLabel>
							<DataboardStatValue>{jobSummaryQuery.data?.completedJobs || 0}</DataboardStatValue>
						</DataboardStat>

						<DataboardStat>
							<DataboardStatLabel>Total Amount</DataboardStatLabel>
							<DataboardStatValue isTotal>
								{formatServiceJobCurrency(jobSummaryQuery.data?.totalAmount || 0)}
							</DataboardStatValue>
						</DataboardStat>
					</DataboardStats>

					{jobToEdit ? (
						<EditServiceJobModal
							key={jobToEdit.id ?? 'new'}
							initialJob={jobToEdit}
							isOpen={isEditOpen}
							setIsOpen={setIsEditOpen}
							onSave={onEditSave}
							onCancel={onEditCancel}
						/>
					) : null}

					{jobToDelete ? (
						<DeleteModal
							type={DeleteType.ServiceJob}
							item={jobToDelete}
							isOpen={isDeleteOpen}
							setIsOpen={setIsDeleteOpen}
							onConfirm={onDeleteConfirmed}
						/>
					) : null}

					<Drawer open={isDetailsOpen} onOpenChange={setIsDetailsOpen} swipeDirection='right'>
						{jobToView ? <ServiceJobDetailsFlyout job={jobToView} /> : null}
					</Drawer>

					<ExportConfirmationModalV2
						mutationResult={exportJobsMutation}
						modalProps={{
							isOpen: !exportJobsMutation.isIdle,
							onRequestClose: () => exportJobsMutation.reset(),
						}}
						subTitle={
							<>
								<p className={css(dbStyleSheet.noMargin)}>
									The fields we&apos;ll export include: Contact Name, Job Number, Summary, Status, Total, Completed On
								</p>
							</>
						}
					/>
				</Databoard>
			</Column>
		</Page>
	);
};
